import { useEffect, useState } from 'react'
import { api, type Desk, type User } from '../api'
import { RowMenu } from '../components/RowMenu'

/**
 * User administration — TECHNICAL_DESIGN.md §10.1.
 *
 * The capabilities existed and had no screen, which meant a fresh appliance's
 * administrator could sign in and had no way to onboard anybody: the one thing
 * §10.1 says an administrator is for.
 *
 * **The invitation is shown once, and this screen says so.** Only its hash is
 * stored, so it cannot be recovered — it is delivered by the administrator, and
 * losing it means issuing another, which is the correct and cheap failure.
 *
 * **Deactivated accounts stay on the list.** There is no delete: the audit trail
 * keeps resolving to them, and the people whose actions matter most to an
 * auditor are frequently the ones who have left. A list that hid former staff
 * would answer *"who can reach this appliance"* wrongly, in the direction that
 * reads as reassurance.
 *
 * **A role is not a ladder, and the picker does not imply one.** An
 * administrator runs the appliance and cannot move a limit; an approver can move
 * every limit in the firm and cannot configure the connector that proposes
 * changes to them. The description beside each role says what it is for rather
 * than where it sits.
 *
 * **Adding somebody and reading the list are two screens**, on the same argument
 * as funds. They are two jobs at two times: onboarding happens once per person
 * and *who has access* is asked repeatedly, often by somebody who is not adding
 * anybody. On one screen the form sat above the list and pushed it below the
 * fold, so the commonest question was answered by scrolling past a form.
 *
 * **The roles cannot be ticked until the account has a name and an address.**
 * Not validation theatre: what is being decided is *what this person may do*,
 * and a role picked before there is a person is picked about nobody. The form
 * used to invite that in the wrong order — the four roles were the widest and
 * loudest thing on it, so it read as the first question rather than the last.
 */
const ROLES = [
  {
    id: 'viewer',
    label: 'Viewer',
    blurb: 'Can read limits, defined terms and the history of a change. Changes nothing.',
  },
  {
    id: 'author',
    label: 'Author',
    blurb: 'Drafts limits, records documents, confirms reviews and signs off. Nothing takes effect until an approver agrees.',
  },
  {
    id: 'approver',
    label: 'Approver',
    blurb: 'Puts limits into force, declines proposed changes, changes what a term means, and exports.',
  },
  {
    id: 'admin',
    label: 'Administrator',
    blurb: 'Runs the system — accounts, funds, groups, the mailbox. On chosen desks, runs those desks’ funds and groups. Cannot change a limit.',
  },
]

/**
 * Where a person works (decision 74): the whole firm, or some desks. Compliance works
 * across the firm; a portfolio manager on their own desks, where they see and change
 * only those desks' funds. An administrator on desks runs those desks' funds and groups;
 * people, the mailbox and the system stay with an administrator of the whole firm.
 */
function DeskPicker({
  desks,
  chosen,
  onChange,
}: {
  desks: Desk[]
  chosen: string[]
  onChange: (ids: string[]) => void
}) {
  const firm = chosen.length === 0
  return (
    <fieldset className="role-picker">
      <legend>Where they work</legend>
      <label className="checkbox" title="Compliance and anyone who works across every desk.">
        <input type="radio" checked={firm} onChange={() => onChange([])} />
        <span>
          <strong>Whole firm</strong>
          <span className="clause"> Every desk’s funds, limits and verdicts.</span>
        </span>
      </label>
      <label className="checkbox" title="Portfolio managers and their teams.">
        <input
          type="radio"
          checked={!firm}
          onChange={() => desks.length > 0 && onChange([desks[0]!.id])}
        />
        <span>
          <strong>Their desks</strong>
          <span className="clause"> Only the funds their desks run.</span>
        </span>
      </label>
      {!firm && (
        <div className="role-options">
          {desks.map((d) => (
            <label key={d.id} className="checkbox">
              <input
                type="checkbox"
                aria-label={d.code}
                checked={chosen.includes(d.id)}
                onChange={(e) => {
                  const next = e.target.checked ? [...chosen, d.id] : chosen.filter((x) => x !== d.id)
                  if (next.length > 0) onChange(next)
                }}
              />
              <span>
                <strong>{d.code}</strong>
                <span className="clause"> {d.name}</span>
              </span>
            </label>
          ))}
        </div>
      )}
    </fieldset>
  )
}

/** The reference card. It sits on both screens because both ask the reader to
 *  pick roles, and a definition on the other screen is a definition nobody
 *  reads. */
function WhatRolesAreFor() {
  return (
    <div className="card">
      <h3>What each role is for</h3>
      <dl className="roles">
        {ROLES.map((r) => (
          <div key={r.id}>
            <dt>{r.label}</dt>
            <dd>{r.blurb}</dd>
          </div>
        ))}
      </dl>
      <p className="note">
        These are not ranks, and one person can hold several. In a small team
        the same person is often the author and the approver, and there is
        nobody else to be either.
      </p>
      <p className="note">
        What that cannot become is signing off your own work:{' '}
        <strong>nobody can approve a change they proposed</strong>. Somebody
        else has to look at it, whatever roles either of them holds.
      </p>
    </div>
  )
}

export function Users({
  mode = 'list',
  onOwnRolesChanged,
}: {
  mode?: 'add' | 'list'
  onOwnRolesChanged?: () => void
}) {
  const [users, setUsers] = useState<User[]>([])
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState<{ email: string; name: string; roles: string[]; desks: string[] }>({
    email: '',
    name: '',
    roles: ['viewer'],
    desks: [],
  })
  const [desks, setDesks] = useState<Desk[]>([])
  /** The account whose desks are being changed, and the desks chosen. */
  const [placing, setPlacing] = useState<User | null>(null)
  const [placed, setPlaced] = useState<string[]>([])
  const [issued, setIssued] = useState<{ email: string; token: string } | null>(null)

  async function load() {
    setError(null)
    try {
      setUsers(await api.users())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoaded(true)
    }
  }

  useEffect(() => {
    void load()
    api
      .desks()
      .then(setDesks)
      .catch(() => setDesks([]))
  }, [])


  async function act(work: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await work()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  function create(event: React.FormEvent) {
    event.preventDefault()
    void act(async () => {
      const created = await api.createUser(form.email, form.name, form.roles, form.desks)
      setIssued({ email: form.email, token: created.invitation_token })
      setForm({ email: '', name: '', roles: ['viewer'], desks: [] })
    })
  }

  /** Who this account is for. Until both are answered there is nobody to give a
   *  role to, so the roles wait. */
  const named = form.email.trim() !== '' && form.name.trim() !== ''

  if (mode === 'list' && !loaded) return <p className="loading">Loading…</p>

  /* Shown on both screens: created here, re-issued from the list, and in both
     cases the one moment the token exists in a form anybody can read. */
  const invitation = issued && (
    <div className="callout">
      <h3>Invitation for {issued.email}</h3>
      <p className="secret">
        <code>{issued.token}</code>
      </p>
      <p className="note">
        Send this to them. It works once, expires in three days, and is shown
        only now — it cannot be looked up again. If it goes astray, issue a new
        one.
      </p>
      <button onClick={() => setIssued(null)}>Done</button>
    </div>
  )

  if (mode === 'add') {
    return (
      <section>
        <header className="page-head">
          <h2>Add</h2>
          <p className="note">
            Creating an account issues an invitation. You do not set their
            password and it is never sent to you — they choose their own when
            they use it.
          </p>
        </header>

        {error && <p className="error">{error}</p>}
        {invitation}

        <div className="card">
          {/* Stacked, not a row: the questions are asked in order — who is this,
              then what may they do — and a row put the widest and loudest of the
              three first. */}
          <form className="stack-form" onSubmit={create}>
            <div className="who">
              <label>
                Email
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </label>
              <label>
                Name
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </label>
            </div>

            <fieldset className="role-picker" disabled={!named}>
              <legend>What they can do</legend>
              <p>
                <button
                  type="button"
                  className="ghost"
                  title="Author and approver across the whole firm: proposes and approves every desk’s limits, and reviews verdicts."
                  onClick={() => setForm({ ...form, roles: ['author', 'approver'], desks: [] })}
                >
                  Compliance officer
                </button>
              </p>
              {!named && (
                <p className="note" data-testid="roles-locked">
                  Give the email and name first. A role is a decision about a
                  person, and there is not one yet.
                </p>
              )}
              {/* The blurb is the point of the picker, so it gets the width. On
                  one column each role wrapped to four lines and the reader met a
                  wall; the checkbox and its words stay on one line either way. */}
              <div className="role-options">
                {ROLES.map((r) => (
                  <label key={r.id} className="checkbox">
                    <input
                      type="checkbox"
                      checked={form.roles.includes(r.id)}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          roles: e.target.checked
                            ? [...form.roles, r.id]
                            : form.roles.filter((held) => held !== r.id),
                        })
                      }
                    />
                    <span>
                      <strong>{r.label}</strong>
                      <span className="clause"> {r.blurb}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            {named && (
              <DeskPicker
                desks={desks}
                chosen={form.desks}
                onChange={(ids) => setForm({ ...form, desks: ids })}
              />
            )}

            <div className="actions">
              <button type="submit" disabled={busy || !named || form.roles.length === 0}>
                Create and invite
              </button>
            </div>
          </form>
          <p className="note">
            People sign in through your firm’s identity provider where one is set up,
            under your firm’s own sign-in policy, or with the password they choose here.
          </p>
        </div>

        <WhatRolesAreFor />
      </section>
    )
  }

  return (
    <section>
      <header className="page-head">
        <h2>People</h2>
        <p className="note">
          Every account on this system. Accounts are never deleted, because the
          activity record has to keep naming who did what, so people who have
          left appear here as deactivated. New accounts are created under{' '}
          <strong>People → Add</strong>.
        </p>
      </header>

      {error && <p className="error">{error}</p>}
      {invitation}

      {placing && (
        <div className="card" data-testid="user-desks">
          <h3>Where {placing.name} works</h3>
          <DeskPicker desks={desks} chosen={placed} onChange={setPlaced} />
          <div className="actions">
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  await api.setUserDesks(placing.id, placed)
                  setPlacing(null)
                  onOwnRolesChanged?.()
                })
              }
            >
              Save
            </button>
            <button type="button" className="ghost" onClick={() => setPlacing(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <table className="grid">
        <thead>
          <tr>
            <th>Person</th>
            <th>Role</th>
            <th>Works on</th>
            <th>Status</th>
            <th>Sign-in</th>
            <th>Last seen</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className={u.status === 'deactivated' ? 'muted-row' : undefined}>
              <td>
                <strong>{u.name}</strong>
                <br />
                <span className="clause">{u.email}</span>
              </td>
              <td className="role-cell">
                {ROLES.map((r) => (
                  <label key={r.id} className="checkbox">
                    <input
                      type="checkbox"
                      checked={u.roles.includes(r.id)}
                      disabled={busy || u.status === 'deactivated'}
                      onChange={(e) => {
                        const roles = e.target.checked
                          ? [...u.roles, r.id]
                          : u.roles.filter((held) => held !== r.id)
                        if (roles.length === 0) return
                        void act(async () => {
                          await api.setUserRoles(u.id, roles)
                          // Your own menus change with your roles, and the
                          // signed-in principal is held in memory until
                          // something asks for it again.
                          onOwnRolesChanged?.()
                        })
                      }}
                    />
                    <span>{r.label}</span>
                  </label>
                ))}
              </td>
              <td data-testid="works-on">
                {u.desks && u.desks.length > 0 ? u.desks.join(', ') : <span className="clause">Whole firm</span>}
              </td>
              <td>
                <span className={`pill pill-${u.status}`}>{u.status}</span>
                {u.invitation_outstanding && (
                  <span className="clause"> invitation outstanding</span>
                )}
              </td>
              <td>
                {u.federated ? 'Microsoft' : 'Password'}
              </td>
              <td className="clause">
                {u.last_login_at ? u.last_login_at.slice(0, 10) : 'never'}
              </td>
              <td className="run-actions">
                {u.status !== 'deactivated' && (
                  <RowMenu
                    label={`Actions for ${u.email}`}
                    disabled={busy}
                    items={[
                      ...(u.status === 'invited'
                        ? [
                            {
                              label: 'New invitation',
                              onSelect: () =>
                                void act(async () => {
                                  const again = await api.reissueInvitation(u.id)
                                  setIssued({ email: u.email, token: again.invitation_token })
                                }),
                            },
                          ]
                        : []),
                      {
                        label: 'Change desks',
                        onSelect: () => {
                          setPlacing(u)
                          setPlaced(desks.filter((d) => (u.desks ?? []).includes(d.code)).map((d) => d.id))
                        },
                      },
                      {
                        label: 'Deactivate',
                        danger: true,
                        onSelect: () => {
                          if (
                            !window.confirm(
                              `Deactivate ${u.email}? They are signed out immediately. ` +
                                'The account is kept so the activity record still names them.',
                            )
                          )
                            return
                          void act(async () => {
                            await api.deactivateUser(u.id)
                          })
                        },
                      },
                    ]}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <WhatRolesAreFor />
    </section>
  )
}
